import { SearchLg } from '@untitledui/icons'
import { type ReactNode, useState } from 'react'

import { createArrayWithLength } from '@/lib/utils'
import { RadioGroup } from '@components/designSystem/Radio'
import { Input } from '@components/designSystem/input/input'
import useTypedTranslation from '@hooks/useTypedTranslation'
import StoreCard from '@routes/StoreSelection/StoreCard'
import { useStoreGrants } from '@state/store.ts'
import { cx } from '@utils/cx.ts'
import { doesStoreMatchSearch } from '@utils/storeSearch'

// Mode 1: Radio behavior (requires confirmation)
interface RadioStoreSelectionProps {
    mode: 'radio'
    selectedStoreId?: string
    onStoreIdSelect: (storeId: string) => void
    confirmBtn: ReactNode
}

// Mode 2: Button behavior (immediate action)
interface ButtonStoreSelectionProps {
    mode: 'button'
    onStorePress: (storeId: string) => void
}

type StoreSelectionProps = RadioStoreSelectionProps | ButtonStoreSelectionProps

function StoreSelection(props: StoreSelectionProps) {
  const { t } = useTypedTranslation()
  const { data: grants, isLoading } = useStoreGrants()
  const [search, setSearch] = useState('')

  const filteredStores = grants
    .map(g => g.store)
    .filter(store => doesStoreMatchSearch(store, search))
    .sort((a, b) => a.name.localeCompare(b.name))

  function inferRenderState() {
    // TODO: (niki/11.09.) this component is wrapped by the user guard which waits for the user to be loaded, so the loading state is currently never shown
    // in the future we might rather have a GET /stores endpoint and load the stores directly here instead of relying on the user object
    if (isLoading) return 'loading'
    if (grants.length === 0) return 'no-stores-available'
    if (filteredStores.length === 0) return 'no-stores-matching'
    return 'show-stores'
  }

  const listState = inferRenderState()

  if (listState === 'no-stores-available'){
    return (
      <div className="flex flex-col items-center justify-center gap-6 px-12 py-20">
        <span className="mb-2 text-center text-2xl font-semibold tracking-wide md:text-3xl">
          {t('storeSelection.noStores.title')}
        </span>
        <span className="text-center text-lg text-gray-500">
          {t('storeSelection.noStores.subtitle')}
        </span>
      </div>
    )
  }

  let selection

  if (props.mode === 'radio'){
    const { selectedStoreId, onStoreIdSelect, confirmBtn } = props
    selection = <>
      <RadioGroup className='overflow-y-auto pb-28' value={selectedStoreId} onChange={onStoreIdSelect}>
        {
          filteredStores.map(store => (
            <StoreCard.Radio key={store.id} store={store} />
          ))}
      </RadioGroup>
      <div
        className={cx(
          'fixed bottom-5 left-1/2 w-1/2 -translate-x-1/2 transition-all duration-300 md:bottom-1/12',
          selectedStoreId ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        )}
      >
        {confirmBtn}
      </div>
    </>
  } else {
    const { onStorePress } = props
    selection = (
      <ul className='flex flex-col gap-3 overflow-y-auto pb-4'>
        {filteredStores.map(store => (
          <li key={store.id}>
            <StoreCard.Button store={store} onClick={() => onStorePress(store.id)} />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className="flex size-full min-h-0 flex-1 flex-col gap-3">
      <Input
        size='lg'
        leadingIcon={{ icon: SearchLg }}
        value={search}
        onChange={setSearch}
        placeholder={t('storeSelection.selectStore.searchPlaceholder')}
      />
      {listState === 'loading' && createArrayWithLength(4, (index) => <StoreCard.Skeleton key={index} />)}
      {listState === 'show-stores' && selection}
      {listState === 'no-stores-matching' && (
        <span className="w-full text-center text-gray-500">
          {t('storeSelection.selectStore.noStoresFound')}
        </span>
      )}
    </div>
  )
}

export default StoreSelection